import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { listQuoteTemplateHistory, restoreQuoteTemplateVersion } from "../lib/api";
import { formatApiError } from "../lib/auth";

// Quote template version history (reconstructed — source not in kit).
// Lists snapshots from GET /quote-templates/:id/history and restores one
// via POST /quote-templates/:id/restore/:versionId.
export default function QuoteTemplateHistoryDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  onRestored,
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (!open || !templateId) return;
    setLoading(true);
    listQuoteTemplateHistory(templateId)
      .then((data) => setVersions(data?.versions || []))
      .catch((err) => toast.error(formatApiError(err, "Couldn't load version history")))
      .finally(() => setLoading(false));
  }, [open, templateId]);

  const restore = async (v) => {
    setRestoringId(v.id);
    try {
      const restored = await restoreQuoteTemplateVersion(templateId, v.id);
      toast.success("Template restored");
      onRestored?.(restored);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't restore that version"));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-template-history-dialog"
        className="bg-[#111] border-white/15 text-slate-100 max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>Version history{templateName ? ` — ${templateName}` : ""}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Restore a previous version of this quote template. The current
            version is snapshotted first, so restoring is undoable.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {loading && <p className="text-sm text-slate-400">Loading…</p>}
          {!loading && versions.length === 0 && (
            <p className="text-sm text-slate-400">No previous versions yet.</p>
          )}
          {versions.map((v) => (
            <div
              key={v.id}
              data-testid={`quote-template-version-${v.id}`}
              className="flex items-center justify-between gap-3 rounded-md border border-white/15 bg-white/[0.05] px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm text-slate-200 truncate">
                  {v.label || "Snapshot"}
                </p>
                <p className="text-xs text-slate-500">
                  {v.created_at ? new Date(v.created_at).toLocaleString() : ""}
                </p>
              </div>
              <Button
                size="sm"
                data-testid={`quote-template-restore-${v.id}`}
                disabled={restoringId === v.id}
                onClick={() => restore(v)}
                className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-semibold"
              >
                {restoringId === v.id ? "Restoring…" : "Restore"}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
